/**
 * THE one place the app tells the server where the device's clock is
 * (`D87`, `D116`). `use-clock-sync.ts` mounts it; nothing else calls it.
 *
 * Two facts go out, for two different readers, and they are NOT the same
 * fact:
 *
 * - the IANA ZONE (`PUT /auth/me/time-zone`), which the server needs to count
 *   a streak in the person's own week. A zone survives a DST change; a UTC
 *   offset does not, which is why `D87` refused one;
 * - the reminder's UTC minute and the OFFSET it was computed with
 *   (`PUT /me/notification-preferences`). The server stores an hour of the UTC
 *   day so the per-minute job is an indexed equality, and
 *   `preferences.ts` writes down whose job the rewrite is: "the app reports
 *   the device's current offset on every launch, and when it differs from the
 *   stored one it PUTs the whole shape back with the minute recomputed from
 *   the local hour". Until this file existed, nothing did — `deviceUtcOffset`
 *   had exactly one caller, the reminder editor, so a person who flew, or who
 *   crossed a DST boundary, kept being reminded at the home hour until they
 *   happened to reopen that editor.
 *
 * ── NO REACT, NO BROWSER ────────────────────────────────────────────────────
 * Every edge is a port — the fetcher, the clock, the zone, the cache, the
 * log — so the whole sequence runs in a Node test with a hand-written
 * fetcher, the way `features/draft/generate.ts` does. The hook supplies
 * `localStorage` and `Intl`.
 *
 * ── THE CACHE IS WHAT KEEPS THIS TO ONE REQUEST PER CHANGE ──────────────────
 * Both halves remember what they last sent and send nothing while it still
 * matches. Written only on SUCCESS, so a launch with no network retries at
 * the next one rather than recording a write that never landed. The offset
 * cache is the more valuable of the two: without it every launch would GET
 * the preferences just to discover they are already right.
 */

import type { AuthorizedFetcher } from "@repo/client/data/core";
import { setTimeZone } from "@repo/client/data/me";
import {
  fetchNotificationPreferences,
  putNotificationPreferences,
} from "@repo/client/data/notifications";
import type {
  NotificationPreferences,
  NotificationPreferencesOutput,
} from "@repo/contract/notifications";
import {
  deviceUtcOffset,
  localMinuteFromUtc,
  utcMinuteFromLocal,
} from "./editors/reminder-model";

/** The zone last accepted by the server, per origin (see the hook). */
export const CLOCK_ZONE_KEY = "mpg.clock.zone";
/** The device offset the preferences were last reconciled against. */
export const CLOCK_OFFSET_KEY = "mpg.clock.offset";

/** `setTimeZoneInput`'s bound. A longer name is refused, forever. */
const MAX_ZONE_LENGTH = 64;

/**
 * What a runtime reports when it has no IANA name to give, both refused by the
 * server as `account.time_zone_invalid`, forever:
 *
 * - an OFFSET — `"+00:00"`, `"-03:00"`. Current V8 answers this for a device
 *   whose zone is not a tz-database name: the Android container, set to
 *   `GMT`, reports `"+00:00"`, and it drew 65 refusals from the local API
 *   between 2026-09-19 and 2026-09-22, one per launch and resume. An offset
 *   is DST-blind, which is exactly why `D87` refuses it;
 * - `"Etc/Unknown"`, ICU's name for "could not determine".
 */
const UNNAMED_ZONE = /^[+-]\d{2}:?\d{2}$|^Etc\/Unknown$/;

/**
 * What the reminder's stored shape should become on a device whose offset has
 * moved, or `null` when there is nothing to rewrite.
 *
 * `null` in the two cases that are not a move:
 * - the stored offset IS the device's, so the stored minute is already right;
 * - no hour was ever chosen (`reminderUtcMinute === null`), so there is no
 *   local hour to preserve. The editor sends the offset when one is picked.
 *
 * Otherwise the LOCAL hour the person chose is recovered with the offset it
 * was stored under and re-expressed under the device's — 20:00 in São Paulo
 * (−180) is 23:00 UTC; the same 20:00 in Lisbon (+60) is 19:00 UTC.
 *
 * Every other field is copied because the PUT REPLACES THE WHOLE SHAPE: the
 * two toggles this file never shows, and `reminderWeekdays`, which is already
 * in local days and so needs no conversion. Built from the GET's own answer,
 * never from a partial shape — the input is strict, and a missing field is a
 * 422 rather than a merge.
 */
export function reminderResync(
  prefs: NotificationPreferencesOutput,
  deviceOffset: number
): NotificationPreferences | null {
  if (
    prefs.utcOffsetMinutes === deviceOffset ||
    prefs.reminderUtcMinute === null
  ) {
    return null;
  }
  const localMinute = localMinuteFromUtc(
    prefs.reminderUtcMinute,
    prefs.utcOffsetMinutes
  );
  return {
    reminderEnabled: prefs.reminderEnabled,
    reminderUtcMinute: utcMinuteFromLocal(localMinute, deviceOffset),
    reminderWeekdays: prefs.reminderWeekdays ?? null,
    sorenessEnabled: prefs.sorenessEnabled,
    summaryEnabled: prefs.summaryEnabled,
    utcOffsetMinutes: deviceOffset,
  };
}

/**
 * The zone to send, or `null` for "the server already has this one".
 *
 * A device with no zone to report sends nothing rather than a guess: the
 * column stays `null`, which the server already treats as "count in UTC".
 * A name past the wire's 64 is dropped here rather than sent — the server
 * would answer 422, the cache would (correctly) not be written, and the app
 * would retry the same refusal at every resume for as long as that phone
 * reports it. An offset or `Etc/Unknown` is dropped for the same reason
 * (`UNNAMED_ZONE`), and `null` is also the right answer there on its own
 * terms: a device that cannot name its zone has no zone to report.
 */
export function timeZoneToSend(
  deviceZone: string | null,
  lastSentZone: string | null
): string | null {
  if (
    deviceZone === null ||
    deviceZone === "" ||
    deviceZone.length > MAX_ZONE_LENGTH ||
    UNNAMED_ZONE.test(deviceZone) ||
    deviceZone === lastSentZone
  ) {
    return null;
  }
  return deviceZone;
}

/** The two cached strings. `read` answers `null` when storage is unavailable. */
export interface ClockStore {
  read(key: string): string | null;
  write(key: string, value: string): void;
}

export interface ClockSyncPorts {
  readonly api: AuthorizedFetcher;
  readonly now: () => Date;
  /** Every failure, swallowed. See {@link syncClock}. */
  readonly onFailure: (what: string, error: unknown) => void;
  readonly store: ClockStore;
  /** The device's IANA zone, or `null` when the runtime will not say. */
  readonly zone: () => string | null;
}

async function syncTimeZone(ports: ClockSyncPorts): Promise<void> {
  const zone = timeZoneToSend(ports.zone(), ports.store.read(CLOCK_ZONE_KEY));
  if (zone === null) {
    return;
  }
  await setTimeZone(ports.api, { timeZone: zone });
  ports.store.write(CLOCK_ZONE_KEY, zone);
}

async function syncReminderOffset(ports: ClockSyncPorts): Promise<void> {
  const deviceOffset = deviceUtcOffset(ports.now());
  const cached = String(deviceOffset);
  if (ports.store.read(CLOCK_OFFSET_KEY) === cached) {
    return;
  }
  const prefs = await fetchNotificationPreferences(ports.api);
  const body = reminderResync(prefs, deviceOffset);
  if (body !== null) {
    await putNotificationPreferences(ports.api, body);
  }
  // Cached even when nothing was sent: the question "is the stored offset the
  // device's?" has been answered, and the answer only changes when the device
  // moves.
  ports.store.write(CLOCK_OFFSET_KEY, cached);
}

/**
 * A run in flight, shared: `StrictMode`'s double effect and a remount join it
 * instead of sending the whole sequence twice (the `generate.ts` idiom).
 */
let inflight: Promise<void> | null = null;

async function runSync(ports: ClockSyncPorts): Promise<void> {
  // Caught SEPARATELY, and in this order. The zone is the newer route: an
  // app that reaches a server deployed before `T14` gets a 404 here, and the
  // reminder rewrite — which every server has answered for far longer — must
  // still happen. Neither failure caches, so both retry at the next launch.
  try {
    await syncTimeZone(ports);
  } catch (error: unknown) {
    ports.onFailure("time zone", error);
  }
  try {
    await syncReminderOffset(ports);
  } catch (error: unknown) {
    ports.onFailure("reminder offset", error);
  }
}

/**
 * Report the clock. Never rejects: this runs at launch and on every resume,
 * and an offline launch is the ordinary case, not an error worth surfacing.
 */
export function syncClock(ports: ClockSyncPorts): Promise<void> {
  if (inflight) {
    return inflight;
  }
  const run = runSync(ports);
  inflight = run;
  const clear = () => {
    if (inflight === run) {
      inflight = null;
    }
  };
  run.then(clear, clear);
  return run;
}
